<template>
    <nav :class="{mobileNavOpen}">
        <div class="logo" :class="{'ml-2': !isMobile}">
            <router-link v-if="previousView && isMobile"
                         :to="previousView"
                         class="back-button">
                {{ $t("general.back") }}
            </router-link>
            <router-link v-else to="/"
                         style="display: flex; align-items: center">
                <img src="../../assets/brand.svg" alt="Logo"/>
            </router-link>
        </div>
        <div class="links" :class="{'mr-2': !isMobile}">
            <div>
                <div v-if="isMobile"
                     class="mobile"
                     @click="mobileNavOpen = !mobileNavOpen">
                    <MobileMenuButton :open="mobileNavOpen"/>
                </div>
                <div class="navigation">
                    <router-link to="/">
                        <img v-if="isMobile"
                             src="../../assets/icons/account-white.svg"
                             alt="Store Icon"/>
                        {{ $t("navigation.users") }}
                    </router-link>
                    <router-link v-if="$store.getters.GET_USER_ROLE !== 'limitedSupport'" to="/restrictions">
                        <img v-if="isMobile"
                             src="../../assets/icons/eye-visible-white.svg"
                             alt="Store Icon"/>
                        {{ $t("navigation.restrictions") }}
                    </router-link>
                    <TDSSpinner v-if="isLoading && !isMobile"
                                :size="20"
                                color="#ffffff"
                                style="margin-top: 10px; margin-left: 24px"/>
                    <div v-else class="link" @click="logout">
                        <img class="logout mr-2"
                             src="../../assets/icons/logout-white.svg"
                             alt="Logout Icon"/>
                        {{ isMobile ? $t("navigation.logout") : "" }}
                    </div>
                    <DefaultFooter v-if="isMobile"/>
                </div>
            </div>
        </div>
    </nav>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import { Route } from "vue-router";
import DefaultFooter from "@/components/partials/DefaultFooter.vue";
import MobileMenuButton
    from "@/components/partials/Navigation/MobileMenuButton.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";

@Component({
    components: {TDSSpinner, MobileMenuButton, DefaultFooter}
})
export default class DefaultNavigationBar extends Vue {
    private mobileNavOpen: boolean = false;
    private isLoading: boolean = false;

    get authenticated(): boolean {
        return !!this.$store.state.auth;
    }

    get userBalance(): number {
        return this.$store.state.user?.balance;
    }

    get isMobile(): boolean {
        return this.$store.state.isMobile;
    }

    get previousView(): boolean {
        return this.$store.state.previousView;
    }

    @Watch("$route", {immediate:true, deep:true})
        onUrlChange(newVal: Route){
        this.mobileNavOpen = false;
    }

    @Watch("mobileNavOpen")
    mobileNavChanged(newVal: boolean) {
        if (newVal) document.body.style.position = "fixed";
        else document.body.style.position = "relative";
    }

    @Watch("isMobile")
    mobileChanged(newVal: boolean) {
        if (!newVal) this.mobileNavOpen = false;
    }

    @Watch("authenticated")
    authChanged(newVal: boolean) {
        if (!newVal) this.mobileNavOpen = false;
    }

    public closeMenu(){
        this.mobileNavOpen = !this.mobileNavOpen;
    }

    public async login(): Promise<void> {
        this.isLoading = true;
        try {
            await this.$authenticator.signIn();
        } catch (e) {
            this.$toastr.error(this.$t("navigation.errors.login"), e);
            this.isLoading = false;
        }
    }

    public async logout(): Promise<void> {
        this.isLoading = true;
        try {
            await this.$authenticator.signOut();
        } catch (e) {
            this.$toastr.error(this.$t("navigation.errors.logout"), e);
            this.isLoading = false;
        }
    }
}
</script>

<style lang="scss" scoped>
nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 74px;
    transition: height 0.5s ease-in-out;
    margin-bottom: 10px;

    .logo {
        display: flex;

        img {
            height: 32px;
            width: auto;
            margin: auto 0;
        }

        .back-button {
            @include backButton($imgPath: "../../");
            margin: -6px 0 0 0;
        }
    }

    .links {
        > div {
            display: flex;
            color: white;

            img, svg {
                margin-right: 5px;

                &.logout {
                    margin-right: 0;
                }
            }

            .navigation {
                display: flex;

                > .link img {
                    display: block;
                }
            }

            a, .link {
                display: flex;
                padding: 8px;
                margin-left: 24px;

                &:link, &:visited {
                    color: white;
                }

                &:hover, &:active {
                    color: white;
                    text-decoration: underline;
                }

                &:hover {
                    cursor: pointer;
                }

                &.account {
                    background: rgba(0, 0, 0, 0.1);
                    border-radius: 10px;

                    img {
                        display: block;
                    }
                }

                &.router-link-active {
                    font-weight: bold;
                }
            }
        }
    }
}

@media only screen and (max-width: $breakPoint - 1px) {
    nav {
        height: 64px;
        padding: 0 15px;
        align-items: flex-start;
        padding-top: 20px;
        overflow: hidden;
        background: $gradient;
        position: fixed;
        top: 0;
        width: 100vw;
        z-index: 999;
        margin-bottom: 0;

        &.mobileNavOpen {
            height: 100vh;
            overflow-y: scroll;
        }

        .logo img {
            height: 27px;
        }

        .links {
            > div {
                a, .link {
                    padding: 0;
                    line-height: 20px;
                }

                img {
                    margin-right: 12px !important;
                }

                .navigation {
                    position: absolute;
                    left: 0;
                    flex-direction: column;
                    top: 80px;
                    padding: 0 15px;
                    width: 100%;
                    padding-bottom: 100px;
                    min-height: calc(100vh - 80px);

                    a, .link {
                        background: rgba(255, 255, 255, 0.4);
                        font-size: 16px;
                        padding: 12px;
                        margin: 0 0 16px 0;
                        border-radius: 5px;
                        position: relative;

                        &:after {
                            content: "";
                            display: block;
                            width: 8px;
                            height: 100%;
                            background: transparent url(../../assets/icons/globe-nav-chevron-right-white.svg) no-repeat center;
                            background-size: 16px;
                            position: absolute;
                            top: 0;
                            right: 15px;
                        }
                    }

                    .user-card {
                        @include card();
                        color: $fullGrey;
                        display: flex;
                        flex-direction: column;
                        margin: 0 0 24px !important;

                        &:after {
                            background-image: url("../../assets/icons/globe-nav-chevron-right-fullGrey.svg");
                        }

                        h2 {
                            font-size: 18px;
                        }

                        div {
                            display: flex;
                            align-items: flex-start;

                            img {
                                margin-right: 8px;
                            }

                            .balance {
                                display: flex;
                                flex-direction: column;

                                .value {
                                    font-size: 20px;
                                    font-weight: 900;
                                    letter-spacing: -0.31px;
                                    margin-bottom: 4px;
                                }

                                .label {
                                    @include fontCaption();
                                }
                            }
                        }
                    }
                }

                .mobile {
                    display: flex;
                    align-items: center;
                    height: 24px;
                }

                .footer {
                    position: absolute;
                    bottom: 16px;
                    left: 0;
                    height: auto;
                    padding: 0 8px;
                }
            }
        }
    }
}
</style>
